import React, { useContext, useMemo, ReactNode } from 'react';

import useAIAgent from '../../hooks/useAIAgent';

import { AIAgentContext, AIAgentContextType } from './AIAgentContext';

interface Props {
  children: ReactNode;
}

export const useAIAgentContext = () => {
  const context = useContext(AIAgentContext);
  if (!context) {
    throw new Error('useAIAgentContext must be used within AIAgentContextProvider');
  }
  return context;
};

export const AIAgentContextProvider = ({ children }: Props) => {
  const aiAgent = useAIAgent();

  const value: AIAgentContextType = useMemo(
    () => ({
      // State
      text: aiAgent.text,
      setText: aiAgent.setText,
      messages: aiAgent.messages,
      isSubmitting: aiAgent.isSubmitting,
      isLoading: aiAgent.isLoading,
      currentThreadId: aiAgent.currentThreadId,
      error: aiAgent.error,
      threads: aiAgent.threads,
      isLoadingThreads: aiAgent.isLoadingThreads,
      hasMoreThreads: aiAgent.hasMoreThreads,
      isDisabled: aiAgent.isDisabled,
      scrollContainerRef: aiAgent.scrollContainerRef,

      // Actions
      loadMoreThreads: aiAgent.loadMoreThreads,
      selectThread: aiAgent.selectThread,
      startNewConversation: aiAgent.startNewConversation,
      handleDeleteThread: aiAgent.handleDeleteThread,
      handleSubmit: aiAgent.handleSubmit,
    }),
    [
      aiAgent.text,
      aiAgent.setText,
      aiAgent.messages,
      aiAgent.isSubmitting,
      aiAgent.isLoading,
      aiAgent.currentThreadId,
      aiAgent.error,
      aiAgent.threads,
      aiAgent.isLoadingThreads,
      aiAgent.hasMoreThreads,
      aiAgent.isDisabled,
      aiAgent.scrollContainerRef,
      aiAgent.loadMoreThreads,
      aiAgent.selectThread,
      aiAgent.startNewConversation,
      aiAgent.handleDeleteThread,
      aiAgent.handleSubmit,
    ],
  );

  return <AIAgentContext.Provider value={value}>{children}</AIAgentContext.Provider>;
};
